import React, { useEffect, useMemo, useState } from "react";
import { Link , useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { getBranches, getCustomers, getTailoringOrders, checkCashRegister } from "../lib/api";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_OPTIONS = [
  { value: "PENDIENTE_PAGO",    label: "Pendiente de pago" },
  { value: "PRE_PRODUCCION",    label: "Pre-producción" },
  { value: "EN_PRODUCCION",     label: "En producción" },
  { value: "CONFECCIONADO",     label: "Confeccionado" },
  { value: "LISTO_PARA_ENTREGA", label: "Listo para entrega" },
  { value: "ENTREGADO",         label: "Entregado" },
  { value: "CANCELADO",         label: "Cancelado" },
];

const STATUS_COLORS = {
  PENDIENTE_PAGO:     "bg-yellow-100 text-yellow-800",
  PRE_PRODUCCION:     "bg-blue-100 text-blue-800",
  EN_PRODUCCION:      "bg-indigo-100 text-indigo-800",
  CONFECCIONADO:      "bg-purple-100 text-purple-800",
  LISTO_PARA_ENTREGA: "bg-green-100 text-green-800",
  ENTREGADO:          "bg-slate-100 text-slate-700",
  CANCELADO:          "bg-red-100 text-red-700",
};

const TailoringOrders = () => {
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const b = await getBranches();
        setBranches(b || []);
      } catch {}
    })();
  }, []);

  const [loading, setLoading] = useState(false);
  const [cajaOpen, setCajaOpen] = useState(true);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filters, setFilters] = useState({
    status: "",
    customer_id: "",
    seller_id: "",
    branch_id: "",
    q: "",
    date_from: "",
    date_to: "",
  });

  const statusLabel = useMemo(() => {
    const m = new Map(STATUS_OPTIONS.map((x) => [x.value, x.label]));
    return (s) => m.get(s) || s;
  }, []);

  const branchName = useMemo(() => {
    const m = new Map((branches || []).map((b) => [b.id, b]));
    return (branchId) => {
      const b = m.get(branchId);
      if (!b) return branchId || "-";
      return b.code ? `${b.code} - ${b.name}` : b.name;
    };
  }, [branches]);

  const isOverdue = (order) => {
    if (!order?.delivery?.due_date) return false;
    if (["ENTREGADO", "CANCELADO"].includes(order?.status)) return false;

    const due = new Date(order.delivery.due_date);
    if (Number.isNaN(due.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    return due < today;
  };

  const load = async (targetPage = page, targetPerPage = perPage) => {
    try {
      setLoading(true);
      const [cust, ords] = await Promise.all([
        getCustomers(),
        getTailoringOrders(filters, targetPage, targetPerPage),
      ]);
      setCustomers((cust && cust.customers) ? cust.customers : (cust || []));
      setOrders(ords.orders || []);
      setTotalPages(ords.total_pages || 1);
      setTotalRecords(ords.total || 0);
      setPage(targetPage);
      setPerPage(targetPerPage);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Error cargando pedidos");
    } finally {
      setLoading(false);
    }
  };

  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    load(); /* eslint-disable-next-line */
    checkCashRegister().then(res => setCajaOpen(!res.required || res.open)).catch(() => setCajaOpen(true));
  }, []);

  const onFilterChange = (key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
  };

  const applyFilters = async () => {
    setPage(1);
    await load(1, perPage);
  };

  const clearFilters = async () => {
    setFilters({
      status: "",
      customer_id: "",
      seller_id: "",
      branch_id: "",
      q: "",
      date_from: "",
      date_to: "",
    });
    setPage(1);
    setTimeout(() => load(1, perPage), 0);
  };

  return (
    <div className="p-4 space-y-6" data-testid="tailoring-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Pedidos de Confección
          </h1>
          <p className="text-slate-600">
            Pedidos personalizados (no afectan inventario)
          </p>
        </div>
        <Button className="gap-2"
          onClick={() => { if (!cajaOpen) { toast.warning('Abre un turno de caja para crear pedidos'); return; } navigate('/tailoring-orders/new'); }}>
          <Plus className="w-4 h-4" />
          Nuevo pedido
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {isAdmin && (
              <div className="space-y-1">
                <Label>Sucursal</Label>
                <Select
                  value={filters.branch_id || ""}
                  onValueChange={(v) =>
                    onFilterChange("branch_id", v === "ALL" ? "" : v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todas</SelectItem>
                    {(branches || []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.code ? `${b.code} - ` : ""}{b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isAdmin && (
              <div className="space-y-1">
            
                <Label>Vendedor</Label>
                <Select
                  value={filters.seller_id || ""}
                  onValueChange={(v) =>
                    onFilterChange("seller_id", v === "ALL" ? "" : v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    {/* Aquí simple: toma vendedores desde los pedidos cargados */}
                    {[
                      ...new Map(
                        (orders || []).map((o) => [o.seller_id, o.seller_name]),
                      ).entries(),
                    ].map(([id, name]) => (
                      <SelectItem key={id} value={id}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label>Estatus</Label>
              <Select
                value={filters.status || ""}
                onValueChange={(v) =>
                  onFilterChange("status", v === "ALL" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Cliente</Label>
              <Select
                value={filters.customer_id || ""}
                onValueChange={(v) =>
                  onFilterChange("customer_id", v === "ALL" ? "" : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  {(customers || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Buscar</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="cliente, producto o #pedido"
                  value={filters.q}
                  onChange={(e) => onFilterChange("q", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Desde</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => onFilterChange("date_from", e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label>Hasta</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => onFilterChange("date_to", e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={applyFilters} className="gap-2" disabled={loading}>
              <Search className="w-4 h-4" />
              Aplicar
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Listado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-600">
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Código</th>
                  <th className="py-2 pr-4">Sucursal</th>
                  <th className="py-2 pr-4">Cliente</th>
                  <th className="py-2 pr-4">Trabajo</th>
                  {/* <th className="py-2 pr-4">Vendedor</th> */}
                  <th className="py-2 pr-4">Estatus</th>
                  <th className="py-2 pr-4">Entrega</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {(orders || []).map((o) => {
                  const overdue = isOverdue(o);
                  return (
                    <tr
                      key={o.id}
                      className={`border-b ${
                        overdue ? "bg-red-50 text-red-900" : ""
                      }`}
                    >
                      <td className="py-2 pr-4">
                        {o.created_at
                          ? new Date(o.created_at).toLocaleString("es-DO")
                          : ""}
                      </td>
                      <td className="py-2 pr-4">{o.code || "-"}</td>
                      <td className="py-2 pr-4">{branchName(o.branch_id)}</td>
                      <td className="py-2 pr-4 capitalize">{o.customer_name}</td>
                      <td className="py-2 pr-4">
                        {(() => {
                          const hasSubs = (o.sub_orders || []).length > 0;
                          if (hasSubs) {
                            const count = o.sub_orders.length;
                            const svcs = [...new Set(o.sub_orders.flatMap(s => (s.services || []).map(sv => sv.name)))];
                            return (
                              <>
                                <span className="text-slate-700 font-medium">{count} {count === 1 ? 'prenda' : 'prendas'}</span>
                                {svcs.length > 0 && <p className="text-xs text-slate-400">{svcs.join(', ')}</p>}
                              </>
                            );
                          }
                          const name = o?.product_ref?.name || '-';
                          return <span className="text-slate-700 font-medium">{name}</span>;
                        })()}
                      </td>
                      {/* <td className="py-2 pr-4">{o.seller_name}</td> */}
                      <td className="py-2 pr-4">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-600"}`}>
                          {statusLabel(o.status)}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        {o?.delivery?.due_date
                          ? new Date(o.delivery.due_date).toLocaleDateString(
                              "es-DO",
                            )
                          : ""}
                        {overdue && (
                          <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                            Vencido
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Link to={`/tailoring-orders/${o.id}`}>
                          <Button variant="outline" size="sm">
                            Detalles
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}

                {(!orders || orders.length === 0) && (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-6 text-center text-slate-500"
                    >
                      No hay pedidos con esos filtros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Mostrar</span>
              <select
                value={perPage}
                onChange={(e) => { const v = Number(e.target.value); load(1, v); }}
                className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>filas &nbsp;·&nbsp; {totalRecords} pedidos en total</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => load(1, perPage)} disabled={page === 1}
                className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50" title="Primera">«</button>
              <button onClick={() => load(page - 1, perPage)} disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50">‹ Anterior</button>
              <span className="px-4 py-1.5 text-sm text-slate-600">
                Página <strong>{page}</strong> de <strong>{totalPages}</strong>
              </span>
              <button onClick={() => load(page + 1, perPage)} disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Siguiente ›</button>
              <button onClick={() => load(totalPages, perPage)} disabled={page >= totalPages}
                className="px-2 py-1.5 rounded-lg text-sm border border-slate-200 disabled:opacity-40 hover:bg-slate-50" title="Última">»</button>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default TailoringOrders;